import { useState, forwardRef } from 'react'

import Link from '@/components/link-component'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import SearchNavIcon from '@/components/search/nav-icon'
import routes from '@/constants/routes'
import LogoHorizontal from '@/icons/logo-horizontal.svg'
import Close from '@/icons/bwi/close.svg'
import HelpUsefulLinks from '@/components/help/help-useful-links'
import HelpContactForm from '@/components/help/help-contact-form'
import CloudStatus from '@/components/cloud-status'
import { useHasScrolled } from '@/utils/dom'
import colors from '@/constants/colors'
import NavContainer from '@/components/nav-container'
import StaggerList, { StaggerListItem } from '@/components/ui/stagger-list'
import HeaderBanner from '@/components/header-banner'
import HelpSearch from '@/components/help-search'
import { cx } from '@/utils/strings.ts'
import { useTranslations } from '@/utils/locale'
import { useHelpSearchModal } from '@/pages/help-parent'
import { TypeHelpTopNav } from '@/types/ctf'

const mobileLinkStyle = `block py-3 px-5 w-full text-right text-white`

type HelpHeaderProps = {
  hasLeftLinks?: boolean
  hasSearch?: boolean
  twRoot?: string
  headerBanners?: any
  isHelpHome?: boolean
  displayPdfButton?: boolean
  topNavs?: TypeHelpTopNav<'WITHOUT_UNRESOLVABLE_LINKS'>[]
  activeTopNavId?: string
}

const HelpHeader = forwardRef<HTMLDivElement, any>(
  (
    {
      hasLeftLinks = true,
      hasSearch = true,
      twRoot = `bg-indigoBlue`,
      headerBanners,
      isHelpHome = false,
    }: HelpHeaderProps,
    ref
  ) => {
    const { t } = useTranslations()
    const leftLinks = hasLeftLinks
      ? [
          { text: t('Personal'), url: routes.productsPersonal },
          { text: t('Business'), url: routes.productsBusiness },
          { text: t('Download'), url: routes.download },
          { text: t('Pricing'), url: routes.pricingBusiness },
          { text: t('Help'), url: routes.help },
        ]
      : []

    const [topRef, hasScrolled] = useHasScrolled()

    const [isNavOpen, setNavOpen] = useState(false)
    const { openSearch } = useHelpSearchModal()

    return (
      <>
        <div ref={topRef as never} className="h-0" />
        <HeaderBanner banners={headerBanners} isHelpSite />
        <header
          ref={ref}
          className={cx(
            `sticky top-0 left-0 z-50 py-1 text-white transition duration-300 dark:border-secondary-700 border-b`,
            hasScrolled && `shadow-lg`,
            twRoot
          )}
        >
          {/* Mobile Nav */}
          <nav className="relative max-w-7xl mx-auto px-5 flex justify-between items-center py-2 md:hidden">
            <Link to={routes.index} title="Bitwarden homepage">
              <LogoHorizontal className="w-44 text-white" />
            </Link>
            <div className="flex gap-3 items-center">
              <SearchNavIcon backgroundColor={colors.indigoBlue} navigateToSearch={false} onClick={openSearch} />

              <button
                title="Main navigation"
                className="text-white text-4xl leading-[12px] flex flex-col items-center w-10 hover:text-secondary-200"
                type="button"
                onClick={() => setNavOpen(true)}
              >
                <span className="-mt-[18px]">.</span>
                <span>.</span>
                <span>.</span>
              </button>
            </div>

            {/* Mobile Dropdown */}
            <NavContainer
              backgroundColor="bg-indigoBlue dark:bg-secondary-900"
              isDrawerOpen={isNavOpen}
              isDrawerOnly
              position="right"
              onRequestClose={() => {
                setNavOpen(false)
              }}
              showOverlay
            >
              <StaggerList className="list-none py-1" isVisible={isNavOpen}>
                <StaggerListItem className="flex flex-col items-end">
                  <button
                    onClick={() => setNavOpen(false)}
                    title="Close main navigation"
                    className="text-white w-9 h-9 p-2 mx-3 mb-5"
                    type="button"
                  >
                    <Close />
                  </button>
                </StaggerListItem>
                <StaggerListItem>
                  <Link to={routes.login} className={mobileLinkStyle}>
                    Log In
                  </Link>
                </StaggerListItem>
                <StaggerListItem>
                  <Link to={routes.getStarted} className={mobileLinkStyle}>
                    Get Started
                  </Link>
                </StaggerListItem>
                {leftLinks.map((link, i) => {
                  const { text, url } = link

                  return (
                    <StaggerListItem key={i}>
                      <Link to={url} className={mobileLinkStyle}>
                        {text}
                      </Link>
                    </StaggerListItem>
                  )
                })}
                <StaggerListItem>
                  <Link to={routes.contactSales} className={mobileLinkStyle}>
                    Business Sales
                  </Link>
                </StaggerListItem>
                <StaggerListItem>
                  <div className="border-t border-1 border-secondary-500 w-full m-5" />
                  <HelpUsefulLinks
                    className=" mr-5 [&_a]:text-white text-right [&_a]:flex-row-reverse"
                    isHelpHome={isHelpHome}
                  />
                </StaggerListItem>

                <StaggerListItem>
                  <div className="border-t border-1 border-secondary-500 w-full m-5" />
                  <HelpContactForm inHelpDrawer />
                  <div className="border-t border-1 border-secondary-500 w-full m-5" />
                </StaggerListItem>

                {isNavOpen && (
                  <StaggerListItem>
                    <CloudStatus className="rounded-lg mt-6 mx-5 px-5 py-3 [&_h3]:text-indigoBlue dark:[&_h3]:text-secondary-50 [&_h3]:m-0 mb-8 dark:bg-secondary-800" />
                  </StaggerListItem>
                )}
              </StaggerList>
            </NavContainer>
          </nav>

          {/* Desktop Nav */}
          <nav className="hidden max-w-8xl mx-auto py-2 px-5 justify-between gap-4 md:flex">
            <div className="flex grow items-center">
              <div className="flex-none w-[260px] lg:w-[235px] xl:w-[300px]">
                <Link to={routes.index} title="Bitwarden homepage">
                  <LogoHorizontal className="w-44 text-white" />
                </Link>
              </div>
              {hasSearch && (
                <>
                  <div className="md:grow hidden lg:block px-5">
                    <HelpSearch />
                  </div>
                  <div className="block md:hidden">
                    <SearchNavIcon navigateToSearch={false} onClick={openSearch} />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-5 items-center">
              <div className="block lg:hidden">
                <SearchNavIcon navigateToSearch={false} onClick={openSearch} />
              </div>
              <CtaLink
                to={routes.contactSales}
                className="hidden md:block text-sm lg:text-base"
                style={buttonStyles.btnSolid}
                hasDarkBg
              >
                {t('Business sales')}
              </CtaLink>

              <CtaLink
                to={routes.getStarted}
                className="mntn hidden md:block text-sm lg:text-base"
                style={buttonStyles.btnOutlined}
                hasDarkBg
                // onClick={() =>
                //   trackCustomEvent({ category: 'Homepage', action: 'Go to vault sign up', label: 'Get started' })
                // }
              >
                {t('Get started')}
              </CtaLink>
              <CtaLink to={routes.login} style={buttonStyles.btnArrow} hasDarkBg className="text-sm lg:text-base">
                {t('Log In')}
              </CtaLink>
            </div>
          </nav>
        </header>
      </>
    )
  }
)

HelpHeader.displayName = 'HelpHeader'
export default HelpHeader
